import { CtrlApiProvider } from "@repo/components/ctrl";
import { VhalGrpcProvider } from "@repo/components/cuttlefish";
import { CtrlApiClient } from "@repo/sdk";
import {
  HttpProvider,
  useHttpContext,
  type HttpClientMap,
} from "@repo/utils/contexts";
import { useHttpConfig } from "@repo/utils/hooks";
import { httpClient, type HttpClient } from "@repo/utils/lib";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type FC,
  type PropsWithChildren,
} from "react";
import { useAppContext } from "./AppContext";
import { useAuthContext } from "./AuthContext";

/**
 * define the overall client
 */
interface ApiClients extends HttpClientMap {
  base: HttpClient;
  api: HttpClient;
}

// create base http client
const baseClient = httpClient.create({
  headers: {
    accept: "application/json",
  },
});

/**
 *
 * @returns clients object with api client instances
 */
export function useApiContext() {
  const { clients } = useHttpContext<ApiClients>();
  return clients;
}

/**
 * provide a base http configuration/client before authentication
 * @returns
 */
export const BaseApiProvider: FC<PropsWithChildren> = ({ children }) => {
  return (
    <HttpProvider<Pick<ApiClients, "base">>
      clients={{
        base: baseClient,
      }}
    >
      {children}
    </HttpProvider>
  );
};

/**
 * extend the api context with clients using authentication
 * @returns
 */
export const AuthenticatedApiProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    env: { NA_CTRL_API_URL, NA_CTRL_GRPC_URL },
  } = useAppContext();
  const { token, handleUnauthorized } = useAuthContext();
  // Default the gRPC base URL to <ctrl-api>/grpc when not configured.
  const grpcBaseUrl =
    NA_CTRL_GRPC_URL?.trim() || `${NA_CTRL_API_URL.replace(/\/+$/, "")}/grpc`;
  // Stable getToken: token refreshes shouldn't rebuild the VhalGrpcProvider
  // value (which would tear down any open server-streaming subscriptions).
  // The ref holds the latest token; the callback returns it on each call.
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const getVhalToken = useCallback(() => tokenRef.current ?? "", []);
  const { mutate } = useHttpConfig();

  // refetch on token change
  // todo: refine this
  useEffect(() => {
    if (token) {
      mutate(() => true);
    }
  }, [token]);

  // create ctrl client for the CtrlApiProvider and associated hooks in the components package
  const ctrlConfig = useMemo(() => ({
    baseURL: NA_CTRL_API_URL,
    ...(token && { authentication: { type: "bearer" as const, token } }),
    onUnauthorized: handleUnauthorized,
  }), [NA_CTRL_API_URL, token, handleUnauthorized]);

  const ctrlClient = useMemo(() => {
    return new CtrlApiClient(ctrlConfig);
  }, [ctrlConfig]);

  // note this is not really used since packages/components is
  // now the context provider for the CtrlApiClient, but this is here in case
  // we want to use other arbitrary http calls on the api
  const apiClient = useMemo(
    () =>
      baseClient.extend({
        prefixUrl: NA_CTRL_API_URL,
        headers: {
          authorization: `Bearer ${token}`,
          // `x-app-id`: @todo
        },
      }),
    [NA_CTRL_API_URL, token]
  );

  return (
    <HttpProvider<Pick<ApiClients, "api">>
      clients={{
        api: apiClient,
      }}
    >
      <CtrlApiProvider client={ctrlClient} config={ctrlConfig}>
        <VhalGrpcProvider baseUrl={grpcBaseUrl} getToken={getVhalToken}>
          {children}
        </VhalGrpcProvider>
      </CtrlApiProvider>
    </HttpProvider>
  );
};
